<script>
import AppPagination from '@/components/common/AppPagination.vue'
import { getPresignedUrl, uploadAttachment } from '@/api/attachment.js'
import {
  createMediaHubCategory,
  createMediaHubDisplayCategory,
  createManualMediaHubContent,
  createMediaSource,
  deleteMediaSource,
  exposeMediaHubContent,
  getHomepageExposures,
  getMediaHubCategories,
  getMediaHubContents,
  getMediaHubDisplayCategories,
  getMediaSources,
  previewMediaHubContentUrl,
  runMediaHubFetch,
  runMediaSourceFetch,
  toggleMediaHubDisplayCategoryActive,
  unexposeMediaHubContent,
  updateMediaHubCategory,
  updateMediaHubDisplayCategory,
  updateMediaSource,
} from '@/api/mediaHub.js'
import { getAdminInfo } from '@/utils/token.js'

const YN_OPTIONS = [
  { title: '전체', value: '' },
  { title: '사용', value: 'Y' },
  { title: '미사용', value: 'N' },
]

const FETCH_METHOD_OPTIONS = [
  { title: 'API', value: 'API' },
  { title: 'RSS', value: 'RSS' },
  { title: 'CRAWL', value: 'CRAWL' },
  { title: 'MANUAL', value: 'MANUAL' },
]

const DISPLAY_AREA_OPTIONS = [
  { title: '미디어허브', value: 'MEDIA_HUB' },
  { title: '메인', value: 'MAIN' },
  { title: '지점 페이지', value: 'BRANCH_PAGE' },
]

const FETCH_STATUS_OPTIONS = [
  { title: '전체', value: '' },
  { title: '신규', value: 'NEW' },
  { title: '승인', value: 'APPROVED' },
  { title: '노출', value: 'EXPOSED' },
  { title: '반려', value: 'REJECTED' },
]

const CONTENT_TYPE_OPTIONS = [
  { title: '전체', value: '' },
  { title: '뉴스', value: 'NEWS' },
  { title: '동영상', value: 'VIDEO' },
  { title: '쇼츠', value: 'SHORTS' },
  { title: '클립', value: 'CLIP' },
  { title: '블로그', value: 'BLOG' },
  { title: '상품정보', value: 'PRODUCT' },
  { title: '게시물', value: 'POST' },
  { title: '공지/이벤트', value: 'NOTICE' },
  { title: '라이브', value: 'LIVE' },
  { title: '기타', value: 'ETC' },
]

export default {
  name: 'MediaHubPage',
  components: { AppPagination },
  data() {
    return {
      tab: this.$route.meta?.mediaHubSection || 'sourceCategories',
      adminInfo: {},
      sourceSearch: { keyword: '', mediaHubCategoryCode: '', autoFetchYn: '', activeYn: 'Y' },
      sources: [],
      sourceTotal: 0,
      sourcePage: 1,
      sourceSize: 10,
      sourceLoading: false,
      sourceDialog: false,
      sourceSaving: false,
      sourceEditTarget: null,
      sourceForm: this.emptySourceForm(),
      sourceDeleteDialog: false,
      sourceDeleteTarget: null,
      sourceDeleting: false,
      categories: [],
      categoryDialog: false,
      categorySaving: false,
      categoryEditTarget: null,
      categoryForm: this.emptyCategoryForm(),
      displayCategories: [],
      displayCategoryDialog: false,
      displayCategorySaving: false,
      displayCategoryUploading: false,
      displayCategoryEditTarget: null,
      displayCategoryForm: this.emptyDisplayCategoryForm(),
      displayCategoryBannerPreviewUrl: '',
      displayCategoryBannerFile: null,
      contentSearch: { keyword: '', mediaHubCategoryCode: '', contentType: '', fetchStatus: '' },
      contents: [],
      contentTotal: 0,
      contentPage: 1,
      contentSize: 10,
      contentLoading: false,
      exposureSearch: { keyword: '', mediaHubCategoryCode: '', mainExposeYn: '' },
      exposures: [],
      exposureTotal: 0,
      exposurePage: 1,
      exposureSize: 10,
      exposureLoading: false,
      contentDetailDialog: false,
      contentDetailTarget: null,
      contentExposeSaving: false,
      contentExposeForm: this.emptyContentExposeForm(),
      manualContentDialog: false,
      manualContentSaving: false,
      manualContentPreviewLoading: false,
      manualContentPreviewMessage: '',
      manualContentForm: this.emptyManualContentForm(),
      fetchRunning: false,
      lastFetchResult: null,
      ynOptions: YN_OPTIONS,
      fetchMethodOptions: FETCH_METHOD_OPTIONS,
      displayAreaOptions: DISPLAY_AREA_OPTIONS,
      fetchStatusOptions: FETCH_STATUS_OPTIONS,
      contentTypeOptions: CONTENT_TYPE_OPTIONS,
    }
  },
  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    sourceCategoryOptions() {
      return [
        { title: '전체', value: '' },
        ...this.categories.map(item => ({
          title: item.categoryName,
          value: item.mediaHubCategoryCode,
          sourceType: item.sourceType,
        })),
      ]
    },
    displayCategoryOptions() {
      return this.displayCategories
        .filter(item => item.activeYn === 'Y')
        .map(item => ({
          title: item.displayCategoryName,
          value: item.mediaHubDisplayCategoryCode,
          displayArea: item.displayArea,
        }))
    },
    exposureCategoryOptions() {
      return [
        { title: '전체', value: '' },
        ...this.displayCategories.map(item => ({
          title: item.displayCategoryName,
          value: item.mediaHubDisplayCategoryCode,
        })),
      ]
    },
    sourceDialogTitle() {
      return this.sourceEditTarget ? '수집 사이트 수정' : '수집 사이트 등록'
    },
    categoryDialogTitle() {
      return this.categoryEditTarget ? '수집 카테고리 수정' : '수집 카테고리 등록'
    },
    displayCategoryDialogTitle() {
      return this.displayCategoryEditTarget ? '미디어허브 카테고리 수정' : '미디어허브 카테고리 등록'
    },
    pageTitle() {
      const titleMap = {
        sourceCategories: '수집카테고리 관리',
        sources: '수집사이트 관리',
        displayCategories: '미디어허브 카테고리 관리',
        contents: '수집 콘텐츠 관리',
        exposures: '홈페이지 노출 콘텐츠 관리',
      }
      return titleMap[this.tab] || '미디어허브'
    },
    pageSubtitle() {
      const subtitleMap = {
        sourceCategories: '자동 수집 사이트를 분류하기 위한 수집 카테고리를 관리합니다.',
        sources: '신규 콘텐츠를 자동으로 확인할 사이트와 채널 URL을 관리합니다.',
        displayCategories: '홈페이지 미디어허브에 노출할 카테고리와 정렬 기준을 관리합니다.',
        contents: '자동 수집된 콘텐츠를 확인하고 홈페이지 노출 전 검토합니다.',
        exposures: '홈페이지에 노출 중인 콘텐츠의 카테고리, 순서, 노출 기간과 조회수를 관리합니다.',
      }
      return subtitleMap[this.tab] || '미디어허브 관리 메뉴입니다.'
    },
    showFetchButton() {
      return this.isHq && (this.tab === 'sources' || this.tab === 'contents')
    },
    showManualContentButton() {
      return this.isHq && this.tab === 'contents'
    },
    manualCategoryOptions() {
      return this.categories.map(item => ({
        title: item.categoryName,
        value: item.mediaHubCategoryCode,
      }))
    },
  },
  watch: {
    '$route.meta.mediaHubSection'(section) {
      this.tab = section || 'sourceCategories'
      this.loadCurrentSection()
    },
  },
  mounted() {
    this.adminInfo = getAdminInfo()
    this.loadCurrentSection()
  },
  methods: {
    emptySourceForm() {
      return {
        mediaHubCategoryCode: '',
        sourceType: '',
        sourceName: '',
        sourceUrl: '',
        sourceOwnerName: '',
        sourceKey: '',
        fetchMethod: 'RSS',
        autoFetchYn: 'Y',
        fetchIntervalHours: 24,
        apiRequiredYn: 'N',
        authRequiredYn: 'N',
        activeYn: 'Y',
      }
    },
    emptyCategoryForm() {
      return { sourceType: '', categoryName: '', categoryDesc: '', exposeOrder: 0 }
    },
    emptyDisplayCategoryForm() {
      return {
        displayCategoryName: '',
        displayCategoryDesc: '',
        displayArea: 'MEDIA_HUB',
        bannerImageUrl: '',
        bannerStorageKey: '',
        bannerAltText: '',
        exposeOrder: 0,
      }
    },
    emptyContentExposeForm() {
      return {
        mediaHubDisplayCategoryCode: '',
        exposeArea: 'MEDIA_HUB',
        exposeStartDt: '',
        exposeEndDt: '',
        exposeOrder: 0,
        pinnedYn: 'N',
        exposeYn: 'Y',
        mainExposeYn: 'N',
        alwaysExposeYn: 'Y',
      }
    },
    emptyManualContentForm() {
      return {
        mediaHubCategoryCode: '',
        sourceName: '',
        contentType: 'POST',
        title: '',
        description: '',
        originalUrl: '',
        thumbnailUrl: '',
        publishedDate: '',
        publishedTime: '00:00',
        mediaHubDisplayCategoryCode: '',
        exposeOrder: 0,
        mainExposeYn: 'N',
        alwaysExposeYn: 'Y',
        exposeStartDt: '',
        exposeEndDt: '',
      }
    },
    async loadCurrentSection() {
      if (this.tab === 'sourceCategories') {
        await this.fetchCategories()
        return
      }
      if (this.tab === 'sources') {
        await Promise.all([this.fetchCategories(), this.fetchSources()])
        return
      }
      if (this.tab === 'displayCategories') {
        await this.fetchDisplayCategories()
        return
      }
      if (this.tab === 'contents') {
        await Promise.all([this.fetchCategories(), this.fetchDisplayCategories(), this.fetchContents()])
        return
      }
      if (this.tab === 'exposures') {
        await Promise.all([this.fetchDisplayCategories(), this.fetchExposures()])
      }
    },
    async fetchCategories() {
      const res = await getMediaHubCategories()
      if (res.data?.code === 0) this.categories = res.data.data || []
    },
    async fetchDisplayCategories() {
      const res = await getMediaHubDisplayCategories()
      if (res.data?.code === 0) this.displayCategories = res.data.data || []
    },
    async fetchSources() {
      this.sourceLoading = true
      try {
        const res = await getMediaSources({ ...this.sourceSearch, page: this.sourcePage, size: this.sourceSize })
        if (res.data?.code === 0) {
          this.sources = res.data.data?.items || []
          this.sourceTotal = res.data.data?.total || 0
        }
      } finally {
        this.sourceLoading = false
      }
    },
    async fetchContents() {
      this.contentLoading = true
      try {
        const res = await getMediaHubContents({ ...this.contentSearch, page: this.contentPage, size: this.contentSize })
        if (res.data?.code === 0) {
          this.contents = res.data.data?.items || []
          this.contentTotal = res.data.data?.total || 0
        }
      } finally {
        this.contentLoading = false
      }
    },
    async fetchExposures() {
      this.exposureLoading = true
      try {
        const res = await getHomepageExposures({ ...this.exposureSearch, page: this.exposurePage, size: this.exposureSize })
        if (res.data?.code === 0) {
          this.exposures = res.data.data?.items || []
          this.exposureTotal = res.data.data?.total || 0
        }
      } finally {
        this.exposureLoading = false
      }
    },
    handleSourceSearch() {
      this.sourcePage = 1
      this.fetchSources()
    },
    resetSourceSearch() {
      this.sourceSearch = { keyword: '', mediaHubCategoryCode: '', autoFetchYn: '', activeYn: 'Y' }
      this.sourcePage = 1
      this.fetchSources()
    },
    handleContentSearch() {
      this.contentPage = 1
      this.fetchContents()
    },
    resetContentSearch() {
      this.contentSearch = { keyword: '', mediaHubCategoryCode: '', contentType: '', fetchStatus: '' }
      this.contentPage = 1
      this.fetchContents()
    },
    handleExposureSearch() {
      this.exposurePage = 1
      this.fetchExposures()
    },
    resetExposureSearch() {
      this.exposureSearch = { keyword: '', mediaHubCategoryCode: '', mainExposeYn: '' }
      this.exposurePage = 1
      this.fetchExposures()
    },
    onSourceCategoryChanged(categoryCode) {
      const category = this.categories.find(item => item.mediaHubCategoryCode === categoryCode)
      if (category) this.sourceForm.sourceType = category.sourceType
    },
    openSourceCreate() {
      this.sourceEditTarget = null
      this.sourceForm = this.emptySourceForm()
      this.sourceDialog = true
    },
    openSourceEdit(item) {
      this.sourceEditTarget = item
      this.sourceForm = {
        mediaHubCategoryCode: item.mediaHubCategoryCode || '',
        sourceType: item.sourceType || '',
        sourceName: item.sourceName || '',
        sourceUrl: item.sourceUrl || '',
        sourceOwnerName: item.sourceOwnerName || '',
        sourceKey: item.sourceKey || '',
        fetchMethod: item.fetchMethod || 'RSS',
        autoFetchYn: item.autoFetchYn || 'Y',
        fetchIntervalHours: item.fetchIntervalHours || 24,
        apiRequiredYn: item.apiRequiredYn || 'N',
        authRequiredYn: item.authRequiredYn || 'N',
        activeYn: item.activeYn || 'Y',
      }
      this.sourceDialog = true
    },
    async saveSource() {
      if (!this.sourceForm.mediaHubCategoryCode || !this.sourceForm.sourceName || !this.sourceForm.sourceUrl) return
      this.sourceSaving = true
      try {
        const payload = { ...this.sourceForm, fetchIntervalHours: Number(this.sourceForm.fetchIntervalHours || 24) }
        const res = this.sourceEditTarget
          ? await updateMediaSource(this.sourceEditTarget.mediaSourceCode, payload)
          : await createMediaSource(payload)
        if (res.data?.code === 0) {
          this.sourceDialog = false
          await this.fetchSources()
        }
      } finally {
        this.sourceSaving = false
      }
    },
    openSourceDelete(item) {
      this.sourceDeleteTarget = item
      this.sourceDeleteDialog = true
    },
    async confirmSourceDelete() {
      if (!this.sourceDeleteTarget) return
      this.sourceDeleting = true
      try {
        const res = await deleteMediaSource(this.sourceDeleteTarget.mediaSourceCode)
        if (res.data?.code === 0) {
          this.sourceDeleteDialog = false
          this.sourceDeleteTarget = null
          await this.fetchSources()
        }
      } finally {
        this.sourceDeleting = false
      }
    },
    async runAllFetch() {
      if (!this.isHq) return
      this.fetchRunning = true
      try {
        const res = await runMediaHubFetch()
        if (res.data?.code === 0) {
          this.lastFetchResult = res.data
          if (this.tab === 'sources') {
            await this.fetchSources()
          } else if (this.tab === 'contents') {
            await this.fetchContents()
          }
        }
      } finally {
        this.fetchRunning = false
      }
    },
    async runOneFetch(item) {
      if (!this.isHq) return
      const res = await runMediaSourceFetch(item.mediaSourceCode)
      if (res.data?.code === 0) {
        this.lastFetchResult = res.data
        await this.fetchSources()
      }
    },
    async openManualContentCreate() {
      this.manualContentForm = this.emptyManualContentForm()
      this.manualContentPreviewMessage = ''
      if (!this.categories.length) await this.fetchCategories()
      if (!this.displayCategories.length) {
        await this.fetchDisplayCategories()
      }
      const firstCategory = this.categories[0]
      const firstDisplayCategory = this.displayCategories.find(item => item.activeYn === 'Y')
      if (firstCategory) this.manualContentForm.mediaHubCategoryCode = firstCategory.mediaHubCategoryCode
      if (firstDisplayCategory) this.manualContentForm.mediaHubDisplayCategoryCode = firstDisplayCategory.mediaHubDisplayCategoryCode
      this.manualContentDialog = true
    },
    async saveManualContent() {
      if (!this.manualContentForm.mediaHubCategoryCode || !this.manualContentForm.sourceName || !this.manualContentForm.title || !this.manualContentForm.originalUrl || !this.manualContentForm.mediaHubDisplayCategoryCode) return
      if (this.manualContentForm.alwaysExposeYn === 'N' && (!this.manualContentForm.exposeStartDt || !this.manualContentForm.exposeEndDt)) {
        window.alert('상시노출이 아닌 경우 노출시작일과 노출종료일을 입력해주세요.')
        return
      }
      this.manualContentSaving = true
      try {
        const payload = {
          ...this.manualContentForm,
          publishedDt: this.combineDateTime(this.manualContentForm.publishedDate, this.manualContentForm.publishedTime),
          exposeOrder: Number(this.manualContentForm.exposeOrder || 0),
          exposeStartDt: this.manualContentForm.alwaysExposeYn === 'Y' ? '' : this.manualContentForm.exposeStartDt,
          exposeEndDt: this.manualContentForm.alwaysExposeYn === 'Y' ? '' : this.manualContentForm.exposeEndDt,
        }
        delete payload.publishedDate
        delete payload.publishedTime
        const res = await createManualMediaHubContent(payload)
        if (res.data?.code === 0) {
          this.manualContentDialog = false
          await this.fetchContents()
        } else if (res.data?.message) {
          window.alert(res.data.message)
        }
      } finally {
        this.manualContentSaving = false
      }
    },
    async previewManualContentUrl() {
      if (!this.manualContentForm.originalUrl) return
      this.manualContentPreviewMessage = ''
      this.manualContentPreviewLoading = true
      try {
        const res = await previewMediaHubContentUrl({ originalUrl: this.manualContentForm.originalUrl })
        if (res.data?.code === 0) {
          const data = res.data.data || {}
          this.manualContentForm.contentType = data.contentType || this.manualContentForm.contentType
          this.manualContentForm.title = data.title || this.manualContentForm.title
          this.manualContentForm.description = data.description || this.manualContentForm.description
          this.manualContentForm.thumbnailUrl = data.thumbnailUrl || this.manualContentForm.thumbnailUrl
          this.applyManualPublishedDt(data.publishedDt)
          this.manualContentForm.sourceName = this.manualContentForm.sourceName || this.extractHostName(this.manualContentForm.originalUrl)
          if (res.data.message && !data.title && !data.description && !data.thumbnailUrl) {
            this.manualContentPreviewMessage = res.data.message
          }
        } else if (res.data?.message) {
          this.manualContentPreviewMessage = res.data.message
        }
      } catch (e) {
        this.manualContentPreviewMessage = 'URL 메타정보를 가져오지 못했습니다. 필요한 항목을 직접 입력해주세요.'
      } finally {
        this.manualContentPreviewLoading = false
      }
    },
    extractHostName(url) {
      try {
        return new URL(url).hostname.replace(/^www\./, '')
      } catch (e) {
        return ''
      }
    },
    applyManualPublishedDt(value) {
      const dateTime = this.toDateTimeLocal(value)
      if (!dateTime) return
      const [date, time] = dateTime.split('T')
      this.manualContentForm.publishedDate = date || this.manualContentForm.publishedDate
      this.manualContentForm.publishedTime = time || this.manualContentForm.publishedTime
    },
    combineDateTime(date, time) {
      if (!date) return ''
      return `${date}T${time || '00:00'}:00`
    },
    async openContentDetail(item) {
      this.contentDetailTarget = item
      this.contentExposeForm = this.emptyContentExposeForm()
      if (!this.displayCategories.length) {
        await this.fetchDisplayCategories()
      }
      const defaultCategory = this.displayCategories.find(category => category.activeYn === 'Y')
      if (defaultCategory) {
        this.contentExposeForm.mediaHubDisplayCategoryCode = defaultCategory.mediaHubDisplayCategoryCode
      }
      if (item.mediaHubDisplayCategoryCode) {
        this.contentExposeForm.mediaHubDisplayCategoryCode = item.mediaHubDisplayCategoryCode
        this.contentExposeForm.exposeOrder = item.exposeOrder || 0
        this.contentExposeForm.mainExposeYn = item.mainExposeYn || 'N'
        this.contentExposeForm.alwaysExposeYn = item.alwaysExposeYn || 'Y'
        this.contentExposeForm.exposeStartDt = this.toDateTimeLocal(item.exposeStartDt)
        this.contentExposeForm.exposeEndDt = this.toDateTimeLocal(item.exposeEndDt)
      }
      this.contentDetailDialog = true
    },
    async saveContentExpose() {
      if (!this.contentDetailTarget || !this.contentExposeForm.mediaHubDisplayCategoryCode) return
      if (this.contentExposeForm.alwaysExposeYn === 'N' && (!this.contentExposeForm.exposeStartDt || !this.contentExposeForm.exposeEndDt)) {
        window.alert('상시노출이 아닌 경우 노출시작일과 노출종료일을 입력해주세요.')
        return
      }
      this.contentExposeSaving = true
      try {
        const payload = {
          ...this.contentExposeForm,
          exposeArea: 'MEDIA_HUB',
          exposeYn: 'Y',
          pinnedYn: 'N',
          exposeStartDt: this.contentExposeForm.alwaysExposeYn === 'Y' ? '' : this.contentExposeForm.exposeStartDt,
          exposeEndDt: this.contentExposeForm.alwaysExposeYn === 'Y' ? '' : this.contentExposeForm.exposeEndDt,
          exposeOrder: Number(this.contentExposeForm.exposeOrder || 0),
        }
        const res = await exposeMediaHubContent(this.contentDetailTarget.fetchCode, payload)
        if (res.data?.code === 0) {
          this.contentDetailDialog = false
          if (this.tab === 'exposures') {
            await this.fetchExposures()
          } else {
            await this.fetchContents()
          }
        }
      } finally {
        this.contentExposeSaving = false
      }
    },
    async unexposeContent(item) {
      if (!this.isHq) return
      if (!window.confirm('홈페이지 노출 목록에서 제외하시겠습니까? 제외 후 수집 콘텐츠 관리에서 다시 확인할 수 있습니다.')) return
      const res = await unexposeMediaHubContent(item.homepageContentCode)
      if (res.data?.code === 0) {
        await this.fetchExposures()
      }
    },
    openCategoryCreate() {
      this.categoryEditTarget = null
      this.categoryForm = this.emptyCategoryForm()
      this.categoryDialog = true
    },
    openCategoryEdit(item) {
      this.categoryEditTarget = item
      this.categoryForm = {
        sourceType: item.sourceType || '',
        categoryName: item.categoryName || '',
        categoryDesc: item.categoryDesc || '',
        exposeOrder: item.exposeOrder || 0,
      }
      this.categoryDialog = true
    },
    async saveCategory() {
      if (!this.categoryForm.sourceType || !this.categoryForm.categoryName) return
      this.categorySaving = true
      try {
        const payload = { ...this.categoryForm, sourceType: this.categoryForm.sourceType.trim().toUpperCase() }
        const res = this.categoryEditTarget
          ? await updateMediaHubCategory(this.categoryEditTarget.mediaHubCategoryCode, payload)
          : await createMediaHubCategory(payload)
        if (res.data?.code === 0) {
          this.categoryDialog = false
          await this.fetchCategories()
        }
      } finally {
        this.categorySaving = false
      }
    },
    openDisplayCategoryCreate() {
      this.displayCategoryEditTarget = null
      this.displayCategoryForm = this.emptyDisplayCategoryForm()
      this.resetPendingDisplayCategoryBanner()
      this.displayCategoryDialog = true
    },
    async openDisplayCategoryEdit(item) {
      this.displayCategoryEditTarget = item
      this.displayCategoryForm = {
        displayCategoryName: item.displayCategoryName || '',
        displayCategoryDesc: item.displayCategoryDesc || '',
        displayArea: item.displayArea || 'MEDIA_HUB',
        bannerImageUrl: item.bannerImageUrl || '',
        bannerStorageKey: item.bannerStorageKey || '',
        bannerAltText: item.bannerAltText || '',
        exposeOrder: item.exposeOrder || 0,
      }
      this.resetPendingDisplayCategoryBanner()
      this.displayCategoryDialog = true
      this.displayCategoryBannerPreviewUrl = await this.resolveBannerPreviewUrl(item.bannerStorageKey, item.bannerImageUrl)
    },
    uploadDisplayCategoryBanner(event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      this.resetPendingDisplayCategoryBanner()
      this.displayCategoryBannerFile = file
      this.displayCategoryBannerPreviewUrl = URL.createObjectURL(file)
      this.displayCategoryForm.bannerAltText = this.displayCategoryForm.bannerAltText || file.name
    },
    async uploadPendingDisplayCategoryBanner() {
      if (!this.displayCategoryBannerFile) return
      this.displayCategoryUploading = true
      try {
        const res = await uploadAttachment(this.buildDisplayCategoryBannerPath(), this.displayCategoryBannerFile)
        if (res.data?.code === 0) {
          this.displayCategoryForm.bannerImageUrl = res.data.url
          this.displayCategoryForm.bannerStorageKey = res.data.objectKey
          this.displayCategoryForm.bannerAltText = this.displayCategoryForm.bannerAltText || this.displayCategoryBannerFile.name
        }
      } finally {
        this.displayCategoryUploading = false
      }
    },
    buildDisplayCategoryBannerPath() {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      return `goldnara/media-hub/category/${yyyy}/${mm}/${dd}/`
    },
    async resolveBannerPreviewUrl(storageKey, fallbackUrl) {
      const objectKey = storageKey || this.extractS3ObjectKey(fallbackUrl)
      if (!objectKey) return fallbackUrl || ''
      try {
        const res = await getPresignedUrl(objectKey)
        if (res.data?.code === 0 && res.data?.presignedUrl) {
          return res.data.presignedUrl
        }
      } catch (e) {
        // Fall back to stored URL if presigned lookup fails.
      }
      return fallbackUrl || ''
    },
    extractS3ObjectKey(url) {
      if (!url) return ''
      const marker = '.amazonaws.com/'
      const markerIndex = url.indexOf(marker)
      if (markerIndex < 0) return ''
      return url.substring(markerIndex + marker.length)
    },
    removeDisplayCategoryBanner() {
      this.resetPendingDisplayCategoryBanner()
      this.displayCategoryForm.bannerImageUrl = ''
      this.displayCategoryForm.bannerStorageKey = ''
      this.displayCategoryForm.bannerAltText = ''
      this.displayCategoryBannerPreviewUrl = ''
    },
    resetPendingDisplayCategoryBanner() {
      if (this.displayCategoryBannerPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.displayCategoryBannerPreviewUrl)
      }
      this.displayCategoryBannerFile = null
      this.displayCategoryBannerPreviewUrl = ''
    },
    async saveDisplayCategory() {
      if (!this.displayCategoryForm.displayCategoryName) return
      this.displayCategorySaving = true
      try {
        await this.uploadPendingDisplayCategoryBanner()
        const res = this.displayCategoryEditTarget
          ? await updateMediaHubDisplayCategory(this.displayCategoryEditTarget.mediaHubDisplayCategoryCode, this.displayCategoryForm)
          : await createMediaHubDisplayCategory(this.displayCategoryForm)
        if (res.data?.code === 0) {
          this.displayCategoryDialog = false
          this.displayCategoryBannerFile = null
          this.fetchDisplayCategories()
        }
      } finally {
        this.displayCategorySaving = false
      }
    },
    async toggleDisplayCategory(item) {
      if (!this.isHq) return
      const res = await toggleMediaHubDisplayCategoryActive(item.mediaHubDisplayCategoryCode)
      if (res.data?.code === 0) this.fetchDisplayCategories()
    },
    formatDate(dt) {
      return dt ? String(dt).replace('T', ' ').substring(0, 16) : '-'
    },
    toDateTimeLocal(dt) {
      return dt ? String(dt).replace(' ', 'T').substring(0, 16) : ''
    },
    statusColor(value) {
      return value === 'Y' || value === 'SUCCESS' ? 'primary' : 'grey'
    },
    contentStatusColor(value) {
      if (value === 'NEW') return 'info'
      if (value === 'APPROVED' || value === 'EXPOSED') return 'primary'
      if (value === 'REJECTED') return 'error'
      return 'grey'
    },
    contentTypeLabel(value) {
      return this.contentTypeOptions.find(item => item.value === value)?.title || value || '-'
    },
  },
}
</script>

<template>
  <div class="media-hub-page">
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="admin-page-title">{{ pageTitle }}</div>
        <div class="admin-page-subtitle mt-1">{{ pageSubtitle }}</div>
      </div>
      <div class="d-flex ga-2">
        <v-btn v-if="showManualContentButton" color="secondary" prepend-icon="mdi-plus" @click="openManualContentCreate">수동 콘텐츠 등록</v-btn>
        <v-btn v-if="showFetchButton" color="primary" prepend-icon="mdi-sync" :loading="fetchRunning" @click="runAllFetch">전체 자동수집 실행</v-btn>
      </div>
    </div>

    <v-alert v-if="lastFetchResult" class="mb-4" color="primary" variant="tonal" density="compact">
      수집 완료: 성공 {{ lastFetchResult.successCount }}건, 실패 {{ lastFetchResult.failCount }}건, 저장 {{ lastFetchResult.insertedCount }}건
    </v-alert>

    <v-card rounded="lg" elevation="1">
      <v-window v-model="tab">
        <v-window-item value="sources">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="sourceSearch.keyword" label="검색" placeholder="제목, URL, 운영주체" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleSourceSearch" />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="sourceSearch.mediaHubCategoryCode" :items="sourceCategoryOptions" label="수집 카테고리" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="6" md="2">
                <v-select v-model="sourceSearch.autoFetchYn" :items="ynOptions" label="자동수집" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="6" md="2">
                <v-select v-model="sourceSearch.activeYn" :items="ynOptions" label="사용여부" variant="outlined" density="compact" hide-details />
              </v-col>
            </v-row>
            <div class="d-flex justify-space-between align-center mt-4">
              <div class="text-caption text-grey">총 {{ sourceTotal }}건</div>
              <div class="d-flex ga-2">
                <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="resetSourceSearch">초기화</v-btn>
                <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSourceSearch">검색</v-btn>
                <v-btn v-if="isHq" color="primary" prepend-icon="mdi-plus" @click="openSourceCreate">등록</v-btn>
              </div>
            </div>
          </v-card-text>
          <v-progress-linear v-if="sourceLoading" indeterminate color="primary" />
          <v-table density="compact">
            <thead>
              <tr>
                <th>카테고리</th>
                <th>제목</th>
                <th>URL</th>
                <th>방식</th>
                <th>자동수집</th>
                <th>상태</th>
                <th>마지막 수집</th>
                <th v-if="isHq" class="text-end">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in sources" :key="item.mediaSourceCode">
                <td>{{ item.mediaHubCategoryName || item.sourceType }}</td>
                <td class="font-weight-medium">{{ item.sourceName }}</td>
                <td class="source-url">{{ item.sourceUrl }}</td>
                <td>{{ item.fetchMethod }}</td>
                <td><v-chip size="small" :color="statusColor(item.autoFetchYn)" variant="tonal" label>{{ item.autoFetchYn }}</v-chip></td>
                <td><v-chip size="small" :color="statusColor(item.lastFetchStatus)" variant="tonal" label>{{ item.lastFetchStatus || '-' }}</v-chip></td>
                <td class="text-caption">{{ formatDate(item.lastFetchedDt) }}</td>
                <td v-if="isHq" class="text-end">
                  <v-btn icon="mdi-sync" size="small" variant="text" @click="runOneFetch(item)" />
                  <v-btn icon="mdi-pencil" size="small" variant="text" @click="openSourceEdit(item)" />
                  <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="openSourceDelete(item)" />
                </td>
              </tr>
              <tr v-if="!sourceLoading && !sources.length">
                <td :colspan="isHq ? 8 : 7" class="text-center text-grey py-8">등록된 수집 사이트가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
          <div class="px-4 pb-4">
            <AppPagination :total="sourceTotal" :page="sourcePage" :size="sourceSize" @update:page="p => { sourcePage = p; fetchSources() }" @update:size="s => { sourceSize = s; sourcePage = 1; fetchSources() }" />
          </div>
        </v-window-item>

        <v-window-item value="contents">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="contentSearch.keyword" label="검색" placeholder="콘텐츠 제목, URL, 수집 사이트" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleContentSearch" />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="contentSearch.mediaHubCategoryCode" :items="sourceCategoryOptions" label="수집 카테고리" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="contentSearch.contentType" :items="contentTypeOptions" label="콘텐츠 타입" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="1">
                <v-select v-model="contentSearch.fetchStatus" :items="fetchStatusOptions" label="상태" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="2" class="d-flex justify-end">
                <div class="filter-actions is-inline">
                  <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="resetContentSearch">초기화</v-btn>
                  <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleContentSearch">검색</v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-progress-linear v-if="contentLoading" indeterminate color="primary" />
          <v-table density="compact">
            <thead>
              <tr>
                <th>수집일</th>
                <th>카테고리</th>
                <th>타입</th>
                <th>사이트</th>
                <th>콘텐츠</th>
                <th>발행일</th>
                <th>상태</th>
                <th class="text-end">링크</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in contents" :key="item.fetchCode" class="content-row" @click="openContentDetail(item)">
                <td class="text-caption">{{ formatDate(item.fetchedDt) }}</td>
                <td>{{ item.mediaHubCategoryName || item.sourceType }}</td>
                <td><v-chip size="small" color="secondary" variant="tonal" label>{{ contentTypeLabel(item.contentType) }}</v-chip></td>
                <td>{{ item.mediaSourceName }}</td>
                <td>
                  <div class="content-title">{{ item.title }}</div>
                  <div class="content-desc">{{ item.description || '-' }}</div>
                </td>
                <td class="text-caption">{{ formatDate(item.publishedDt) }}</td>
                <td><v-chip size="small" :color="contentStatusColor(item.fetchStatus)" variant="tonal" label>{{ item.fetchStatus }}</v-chip></td>
                <td class="text-end">
                  <v-btn icon="mdi-eye-outline" size="small" variant="text" @click.stop="openContentDetail(item)" />
                </td>
              </tr>
              <tr v-if="!contentLoading && !contents.length">
                <td colspan="8" class="text-center text-grey py-8">수집된 콘텐츠가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
          <div class="px-4 pb-4">
            <AppPagination :total="contentTotal" :page="contentPage" :size="contentSize" @update:page="p => { contentPage = p; fetchContents() }" @update:size="s => { contentSize = s; contentPage = 1; fetchContents() }" />
          </div>
        </v-window-item>

        <v-window-item value="exposures">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="exposureSearch.keyword" label="검색" placeholder="콘텐츠 제목, URL, 수집 사이트" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleExposureSearch" />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="exposureSearch.mediaHubCategoryCode" :items="exposureCategoryOptions" label="미디어허브 카테고리" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="exposureSearch.mainExposeYn" :items="ynOptions" label="메인 노출" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <div class="filter-actions">
                  <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="resetExposureSearch">초기화</v-btn>
                  <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleExposureSearch">검색</v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-progress-linear v-if="exposureLoading" indeterminate color="primary" />
          <v-table density="compact">
            <thead>
              <tr>
                <th>미디어허브 카테고리</th>
                <th>순서</th>
                <th>콘텐츠</th>
                <th>메인</th>
                <th>노출기간</th>
                <th>수집 사이트</th>
                <th class="text-end">조회수</th>
                <th class="text-end">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in exposures" :key="item.homepageContentCode">
                <td>{{ item.displayCategoryName || '-' }}</td>
                <td>{{ item.exposeOrder }}</td>
                <td>
                  <div class="content-title">{{ item.title }}</div>
                  <div class="content-desc">{{ contentTypeLabel(item.contentType) }} · {{ item.originalUrl }}</div>
                </td>
                <td><v-chip size="small" :color="statusColor(item.mainExposeYn)" variant="tonal" label>{{ item.mainExposeYn }}</v-chip></td>
                <td class="text-caption">
                  <span v-if="item.alwaysExposeYn === 'Y'">상시노출</span>
                  <span v-else>{{ formatDate(item.exposeStartDt) }} ~ {{ formatDate(item.exposeEndDt) }}</span>
                </td>
                <td>{{ item.mediaSourceName }}</td>
                <td class="text-end font-weight-bold">{{ Number(item.viewCount || 0).toLocaleString() }}</td>
                <td class="text-end">
                  <v-btn icon="mdi-pencil" size="small" variant="text" @click="openContentDetail(item)" />
                  <v-btn icon="mdi-eye-off-outline" size="small" variant="text" color="warning" @click="unexposeContent(item)" />
                  <v-btn icon="mdi-open-in-new" size="small" variant="text" :href="item.originalUrl" target="_blank" rel="noopener noreferrer" />
                </td>
              </tr>
              <tr v-if="!exposureLoading && !exposures.length">
                <td colspan="8" class="text-center text-grey py-8">홈페이지에 노출 중인 콘텐츠가 없습니다.</td>
              </tr>
            </tbody>
          </v-table>
          <div class="px-4 pb-4">
            <AppPagination :total="exposureTotal" :page="exposurePage" :size="exposureSize" @update:page="p => { exposurePage = p; fetchExposures() }" @update:size="s => { exposureSize = s; exposurePage = 1; fetchExposures() }" />
          </div>
        </v-window-item>

        <v-window-item value="sourceCategories">
          <v-card-text class="d-flex justify-end">
            <v-btn v-if="isHq" color="primary" prepend-icon="mdi-plus" @click="openCategoryCreate">수집 카테고리 등록</v-btn>
          </v-card-text>
          <v-table density="compact">
            <thead>
              <tr>
                <th>카테고리명</th>
                <th>소스 타입</th>
                <th>설명</th>
                <th>정렬</th>
                <th v-if="isHq" class="text-end">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in categories" :key="item.mediaHubCategoryCode">
                <td class="font-weight-medium">{{ item.categoryName }}</td>
                <td>{{ item.sourceType }}</td>
                <td>{{ item.categoryDesc || '-' }}</td>
                <td>{{ item.exposeOrder }}</td>
                <td v-if="isHq" class="text-end">
                  <v-btn icon="mdi-pencil" size="small" variant="text" @click="openCategoryEdit(item)" />
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-window-item>

        <v-window-item value="displayCategories">
          <v-card-text class="d-flex justify-end">
            <v-btn v-if="isHq" color="primary" prepend-icon="mdi-plus" @click="openDisplayCategoryCreate">노출 카테고리 등록</v-btn>
          </v-card-text>
          <v-table density="compact">
            <thead>
              <tr>
                <th>노출 카테고리명</th>
                <th>영역</th>
                <th>배너</th>
                <th>설명</th>
                <th>정렬</th>
                <th>사용</th>
                <th v-if="isHq" class="text-end">관리</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in displayCategories" :key="item.mediaHubDisplayCategoryCode">
                <td class="font-weight-medium">{{ item.displayCategoryName }}</td>
                <td>{{ item.displayArea }}</td>
                <td>
                  <v-chip size="small" :color="item.bannerImageUrl ? 'primary' : 'grey'" variant="tonal" label>
                    {{ item.bannerImageUrl ? 'Y' : 'N' }}
                  </v-chip>
                </td>
                <td>{{ item.displayCategoryDesc || '-' }}</td>
                <td>{{ item.exposeOrder }}</td>
                <td><v-switch :model-value="item.activeYn === 'Y'" color="primary" density="compact" hide-details @click.stop="toggleDisplayCategory(item)" /></td>
                <td v-if="isHq" class="text-end">
                  <v-btn icon="mdi-pencil" size="small" variant="text" @click="openDisplayCategoryEdit(item)" />
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-window-item>
      </v-window>
    </v-card>

    <v-dialog v-model="sourceDialog" max-width="720" persistent>
      <v-card rounded="lg">
        <v-card-title>{{ sourceDialogTitle }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="sourceForm.mediaHubCategoryCode" :items="sourceCategoryOptions.filter(item => item.value)" label="수집 카테고리 *" variant="outlined" density="comfortable" @update:model-value="onSourceCategoryChanged" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="sourceForm.sourceType" label="소스 타입" variant="outlined" density="comfortable" readonly />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="sourceForm.sourceName" label="제목 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="sourceForm.sourceOwnerName" label="운영주체/지점명" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="sourceForm.sourceUrl" label="URL *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="sourceForm.sourceKey" label="내부 식별값" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="sourceForm.fetchMethod" :items="fetchMethodOptions" label="수집 방식" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="6" md="3">
              <v-select v-model="sourceForm.autoFetchYn" :items="ynOptions.filter(item => item.value)" label="자동수집" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model.number="sourceForm.fetchIntervalHours" label="수집주기(시간)" type="number" min="1" max="24" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="6" md="3">
              <v-select v-model="sourceForm.apiRequiredYn" :items="ynOptions.filter(item => item.value)" label="API 필요" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="6" md="3">
              <v-select v-model="sourceForm.authRequiredYn" :items="ynOptions.filter(item => item.value)" label="인증 필요" variant="outlined" density="comfortable" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="sourceDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="sourceSaving" @click="saveSource">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sourceDeleteDialog" max-width="420">
      <v-card rounded="lg">
        <v-card-title>수집 사이트 삭제</v-card-title>
        <v-card-text>
          <div class="text-body-2">
            <strong>{{ sourceDeleteTarget?.sourceName }}</strong> 수집 사이트를 삭제하시겠습니까?
          </div>
          <div class="text-caption text-grey mt-2">삭제하면 목록에서 미사용 상태로 전환됩니다.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="sourceDeleteDialog = false">취소</v-btn>
          <v-btn color="error" :loading="sourceDeleting" @click="confirmSourceDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="manualContentDialog" max-width="860" persistent>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>수동 콘텐츠 등록</span>
          <v-btn icon="mdi-close" size="small" variant="text" @click="manualContentDialog = false" />
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select v-model="manualContentForm.mediaHubCategoryCode" :items="manualCategoryOptions" label="수집 카테고리 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="manualContentForm.sourceName" label="출처명 *" placeholder="예: Investing.com" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="manualContentForm.contentType" :items="contentTypeOptions.filter(item => item.value)" label="콘텐츠 타입 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="manualContentForm.title" label="콘텐츠 제목 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12">
              <div class="manual-url-row">
                <v-text-field
                  v-model="manualContentForm.originalUrl"
                  label="원문 URL *"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  @keyup.enter="previewManualContentUrl"
                />
                <v-btn
                  color="secondary"
                  variant="tonal"
                  prepend-icon="mdi-cloud-download-outline"
                  :loading="manualContentPreviewLoading"
                  :disabled="!manualContentForm.originalUrl"
                  @click="previewManualContentUrl"
                >
                  가져오기
                </v-btn>
              </div>
              <v-alert v-if="manualContentPreviewMessage" class="mt-2" color="warning" variant="tonal" density="compact">
                {{ manualContentPreviewMessage }}
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="manualContentForm.thumbnailUrl" label="썸네일 URL" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="manualContentForm.publishedDate"
                label="발행일"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-month-outline"
                class="native-picker-field"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="manualContentForm.publishedTime"
                label="발행시간"
                type="time"
                step="1"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-clock-time-four-outline"
                class="native-picker-field"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="manualContentForm.mediaHubDisplayCategoryCode" :items="displayCategoryOptions" label="미디어허브 카테고리 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="manualContentForm.description" label="콘텐츠 설명" rows="4" variant="outlined" density="comfortable" />
            </v-col>
          </v-row>

          <section class="content-review-panel exposure-panel mt-3">
            <div class="content-review-header">
              <div>
                <div class="content-section-eyebrow">홈페이지 노출 설정</div>
                <div class="content-section-title">수동 등록과 동시에 홈페이지 노출 콘텐츠로 등록됩니다.</div>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="manualContentForm.exposeOrder" label="노출 순서" type="number" variant="outlined" density="comfortable" hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <div class="switch-tile">
                  <div>
                    <div class="switch-title">메인 노출</div>
                    <div class="switch-caption">메인 영역에도 노출</div>
                  </div>
                  <v-switch v-model="manualContentForm.mainExposeYn" true-value="Y" false-value="N" color="primary" hide-details density="compact" />
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="switch-tile">
                  <div>
                    <div class="switch-title">상시노출</div>
                    <div class="switch-caption">기간 제한 없음</div>
                  </div>
                  <v-switch v-model="manualContentForm.alwaysExposeYn" true-value="Y" false-value="N" color="primary" hide-details density="compact" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="manualContentForm.exposeStartDt"
                  label="노출 시작일시"
                  type="datetime-local"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="manualContentForm.alwaysExposeYn === 'Y'"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="manualContentForm.exposeEndDt"
                  label="노출 종료일시"
                  type="datetime-local"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="manualContentForm.alwaysExposeYn === 'Y'"
                />
              </v-col>
            </v-row>
          </section>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="manualContentDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="manualContentSaving" @click="saveManualContent">등록</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="contentDetailDialog" max-width="920">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ tab === 'exposures' ? '홈페이지 노출 콘텐츠 수정' : '수집 콘텐츠 검토' }}</span>
          <v-btn icon="mdi-close" size="small" variant="text" @click="contentDetailDialog = false" />
        </v-card-title>
        <v-card-text v-if="contentDetailTarget">
          <div class="content-review-stack">
            <section class="content-review-panel">
              <div class="content-review-header">
                <div>
                  <div class="content-section-eyebrow">수집 데이터</div>
                  <div class="content-section-title">외부 채널에서 가져온 콘텐츠 정보</div>
                </div>
                <div class="d-flex ga-2 flex-wrap justify-end">
                  <v-chip size="small" color="primary" variant="tonal" label>{{ contentDetailTarget.mediaHubCategoryName || contentDetailTarget.sourceType }}</v-chip>
                  <v-chip size="small" :color="contentStatusColor(contentDetailTarget.fetchStatus)" variant="tonal" label>{{ contentDetailTarget.fetchStatus }}</v-chip>
                  <v-chip v-if="contentDetailTarget.contentType" size="small" color="grey" variant="tonal" label>{{ contentTypeLabel(contentDetailTarget.contentType) }}</v-chip>
                </div>
              </div>

              <div class="content-review-body">
                <div class="content-preview-thumb" :class="{ 'is-empty': !contentDetailTarget.thumbnailUrl }">
                  <img v-if="contentDetailTarget.thumbnailUrl" :src="contentDetailTarget.thumbnailUrl" alt="" />
                  <v-icon v-else size="42" color="grey">mdi-image-off-outline</v-icon>
                </div>
                <div class="content-preview-info">
                  <div class="content-detail-title">{{ contentDetailTarget.title }}</div>
                  <div class="content-detail-desc mt-3">{{ contentDetailTarget.description || '설명이 없습니다.' }}</div>

                  <div class="detail-grid mt-5">
                    <div class="detail-label">수집 사이트</div>
                    <div>{{ contentDetailTarget.mediaSourceName || '-' }}</div>
                    <div class="detail-label">작성자</div>
                    <div>{{ contentDetailTarget.authorName || '-' }}</div>
                    <div class="detail-label">발행일</div>
                    <div>{{ formatDate(contentDetailTarget.publishedDt) }}</div>
                    <div class="detail-label">수집일</div>
                    <div>{{ formatDate(contentDetailTarget.fetchedDt) }}</div>
                  </div>

                  <div class="mt-5">
                    <div class="detail-label mb-2">원본 콘텐츠 링크</div>
                    <a class="content-link" :href="contentDetailTarget.originalUrl" target="_blank" rel="noopener noreferrer">
                      {{ contentDetailTarget.originalUrl }}
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <section class="content-review-panel exposure-panel">
              <div class="content-review-header">
                <div>
                  <div class="content-section-eyebrow">홈페이지 노출 설정</div>
                  <div class="content-section-title">사용자 미디어허브에 보여질 위치와 기간을 설정합니다.</div>
                </div>
              </div>

              <v-row dense class="mt-1">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="contentExposeForm.mediaHubDisplayCategoryCode"
                    :items="displayCategoryOptions"
                    label="미디어허브 카테고리 *"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="contentExposeForm.exposeOrder" label="노출 순서" type="number" variant="outlined" density="comfortable" hide-details />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="switch-tile">
                    <div>
                      <div class="switch-title">메인 노출</div>
                      <div class="switch-caption">메인 영역에도 노출</div>
                    </div>
                    <v-switch v-model="contentExposeForm.mainExposeYn" true-value="Y" false-value="N" color="primary" hide-details density="compact" />
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="switch-tile">
                    <div>
                      <div class="switch-title">상시노출</div>
                      <div class="switch-caption">기간 제한 없음</div>
                    </div>
                    <v-switch v-model="contentExposeForm.alwaysExposeYn" true-value="Y" false-value="N" color="primary" hide-details density="compact" />
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="contentExposeForm.exposeStartDt"
                    label="노출 시작일시"
                    type="datetime-local"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    :disabled="contentExposeForm.alwaysExposeYn === 'Y'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="contentExposeForm.exposeEndDt"
                    label="노출 종료일시"
                    type="datetime-local"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    :disabled="contentExposeForm.alwaysExposeYn === 'Y'"
                  />
                </v-col>
              </v-row>
            </section>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="contentDetailDialog = false">?リ린</v-btn>
          <v-btn color="secondary" prepend-icon="mdi-monitor-share" :loading="contentExposeSaving" @click="saveContentExpose">홈페이지 노출 저장</v-btn>
          <v-btn color="primary" prepend-icon="mdi-open-in-new" :href="contentDetailTarget?.originalUrl" target="_blank" rel="noopener noreferrer">원본으로 이동</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="categoryDialog" max-width="520" persistent>
      <v-card rounded="lg">
        <v-card-title>{{ categoryDialogTitle }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="categoryForm.categoryName" label="카테고리명 *" variant="outlined" density="comfortable" />
          <v-text-field v-model="categoryForm.sourceType" label="소스 타입 *" placeholder="예: TIKTOK" variant="outlined" density="comfortable" />
          <v-text-field v-model.number="categoryForm.exposeOrder" label="정렬순서" type="number" variant="outlined" density="comfortable" />
          <v-textarea v-model="categoryForm.categoryDesc" label="설명" rows="3" variant="outlined" density="comfortable" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="categoryDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="categorySaving" @click="saveCategory">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="displayCategoryDialog" max-width="520" persistent>
      <v-card rounded="lg">
        <v-card-title>{{ displayCategoryDialogTitle }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="displayCategoryForm.displayCategoryName" label="노출 카테고리명 *" variant="outlined" density="comfortable" />
          <v-select v-model="displayCategoryForm.displayArea" :items="displayAreaOptions" label="노출 영역" variant="outlined" density="comfortable" />
          <v-text-field v-model.number="displayCategoryForm.exposeOrder" label="정렬순서" type="number" variant="outlined" density="comfortable" />
          <div class="banner-upload-block mb-5">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-2">카테고리 배너</div>
              <div class="d-flex ga-2">
                <input ref="displayCategoryBannerInput" type="file" accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp" hidden @change="uploadDisplayCategoryBanner">
                <v-btn size="small" variant="tonal" prepend-icon="mdi-image-plus" :loading="displayCategoryUploading" @click="$refs.displayCategoryBannerInput?.click()">배너 업로드</v-btn>
                <v-btn v-if="displayCategoryForm.bannerImageUrl" size="small" variant="text" color="error" prepend-icon="mdi-delete-outline" @click="removeDisplayCategoryBanner">삭제</v-btn>
              </div>
            </div>
            <div class="banner-help mb-3">
              사용자 화면의 상단 배너와 동일한 비율로 미리보기됩니다. 중요한 문구와 로고는 중앙 영역에 배치하세요.
            </div>
            <div v-if="displayCategoryBannerPreviewUrl" class="banner-preview-stack">
              <div>
                <div class="banner-preview-label">PC 화면 미리보기</div>
                <div class="banner-preview banner-preview-desktop">
                  <img :src="displayCategoryBannerPreviewUrl" alt="" />
                  <div class="banner-preview-overlay">
                    <span>미디어허브</span>
                    <strong>{{ displayCategoryForm.displayCategoryName || '카테고리명' }}</strong>
                    <small>{{ displayCategoryForm.displayCategoryDesc || '카테고리 설명이 이 위치에 표시됩니다.' }}</small>
                  </div>
                </div>
              </div>
              <div>
                <div class="banner-preview-label">모바일 화면 미리보기</div>
                <div class="banner-preview banner-preview-mobile">
                  <img :src="displayCategoryBannerPreviewUrl" alt="" />
                  <div class="banner-preview-overlay is-mobile">
                    <span>미디어허브</span>
                    <strong>{{ displayCategoryForm.displayCategoryName || '카테고리명' }}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="banner-empty">배너가 없으면 사용자 화면에서 임시 배너가 표시됩니다.</div>
          </div>
          <v-text-field v-model="displayCategoryForm.bannerAltText" label="배너 대체문구" variant="outlined" density="comfortable" />
          <v-textarea v-model="displayCategoryForm.displayCategoryDesc" label="설명" rows="3" variant="outlined" density="comfortable" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="displayCategoryDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="displayCategorySaving" @click="saveDisplayCategory">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.source-url {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #9fb0c9;
}

.content-title {
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.content-desc {
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #8d9bb0;
  font-size: 12px;
}

.content-row {
  cursor: pointer;
}

.content-row:hover {
  background: rgba(205, 178, 122, 0.08);
}

.manual-url-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
}

.native-picker-field :deep(input::-webkit-calendar-picker-indicator) {
  cursor: pointer;
  filter: invert(0.92) brightness(1.2);
  opacity: 0.9;
}

.native-picker-field :deep(input::-webkit-calendar-picker-indicator:hover) {
  opacity: 1;
}

.banner-upload-block {
  border: 1px solid rgba(205, 178, 122, 0.2);
  border-radius: 10px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
}

.banner-help {
  color: #9aa7b8;
  font-size: 12px;
  line-height: 1.6;
}

.banner-preview-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.banner-preview-label {
  margin-bottom: 6px;
  color: #d8bd79;
  font-size: 12px;
  font-weight: 800;
}

.banner-preview {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #101722;
}

.banner-preview::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.68));
  pointer-events: none;
}

.banner-preview-desktop {
  aspect-ratio: 1680 / 380;
}

.banner-preview-mobile {
  width: min(210px, 100%);
  aspect-ratio: 360 / 320;
}

.banner-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.banner-preview-overlay {
  position: absolute;
  left: 16%;
  bottom: 13%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 70%;
  color: #fff;
}

.banner-preview-overlay span {
  font-size: 10px;
  font-weight: 800;
}

.banner-preview-overlay strong {
  font-size: 22px;
  line-height: 1.15;
}

.banner-preview-overlay small {
  font-size: 11px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.8);
}

.banner-preview-overlay.is-mobile {
  left: 10%;
  bottom: 16%;
  max-width: 78%;
}

.banner-preview-overlay.is-mobile strong {
  font-size: 18px;
}

.banner-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  border: 1px dashed rgba(205, 178, 122, 0.24);
  border-radius: 8px;
  color: #9aa7b8;
  font-size: 13px;
}

.content-detail-title {
  color: #eef3fb;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
}

.content-detail-desc {
  color: #aeb9c8;
  line-height: 1.65;
  white-space: pre-wrap;
}

.content-review-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.content-review-panel {
  border: 1px solid rgba(205, 178, 122, 0.18);
  border-radius: 10px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.03);
}

.exposure-panel {
  background: rgba(205, 178, 122, 0.045);
}

.content-review-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.content-section-eyebrow {
  color: #d8bd79;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.content-section-title {
  margin-top: 4px;
  color: #eef3fb;
  font-size: 15px;
  font-weight: 700;
}

.content-review-body {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 18px;
}

.content-preview-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 140px;
  border: 1px solid rgba(205, 178, 122, 0.22);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  aspect-ratio: 16 / 10;
}

.content-preview-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.content-preview-thumb.is-empty {
  border-style: dashed;
}

.content-preview-info {
  min-width: 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 10px 14px;
  color: #d8e0ec;
}

.detail-label {
  color: #8d9bb0;
  font-size: 12px;
  font-weight: 600;
}

.content-link {
  display: block;
  max-width: 100%;
  overflow: hidden;
  color: #d8bd79;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-link:hover {
  text-decoration: underline;
}

.switch-tile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(205, 178, 122, 0.18);
  border-radius: 8px;
  min-height: 56px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  gap: 10px;
}

.switch-title {
  color: #eef3fb;
  font-size: 13px;
  font-weight: 700;
}

.switch-caption {
  margin-top: 2px;
  color: #8d9bb0;
  font-size: 11px;
}

.filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.filter-actions.is-inline {
  width: 100%;
}

.filter-actions :deep(.v-btn) {
  min-width: 92px;
}

@media (max-width: 959px) {
  .filter-actions {
    justify-content: stretch;
  }

  .filter-actions :deep(.v-btn) {
    flex: 1 1 0;
  }

  .content-review-body {
    grid-template-columns: 1fr;
  }

  .content-review-header {
    flex-direction: column;
  }

  .manual-url-row {
    grid-template-columns: 1fr;
  }
}
</style>

